import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';

// Landing page — public, unauthenticated. Header.js suppresses the app
// nav on this route so we get the full page. "Watercolor editorial"
// redesign recreated VERBATIM from
// design_handoff_landing_page/Avayble Landing Page.html — that file is
// the single source of truth for markup, copy (word-for-word), inline
// SVG icon paths, and styles (see Landing.css). Only substitutions:
// router <Link>s per the handoff README (Log in → /login, all CTAs →
// /signup, footer legal → static .html pages), the repo logo at
// /imgs/avayble_logo.png (byte-identical to the bundled one), and
// assets served from /imgs/landing/.

const Landing = () => {
  // Reveal-on-scroll, matching the prototype's <script> exactly:
  // html.js gate class, IntersectionObserver (threshold 0.1, rootMargin
  // '0px 0px -6% 0px', unobserve after reveal), a rAF pass that
  // immediately reveals anything already within 1.1× viewport height,
  // and a 1.2s reveal-everything safety net.
  useEffect(() => {
    document.documentElement.classList.add('js');
    const revs = document.querySelectorAll('.av-landing .reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -6% 0px' });
    revs.forEach(el => io.observe(el));
    const raf = requestAnimationFrame(() => revs.forEach(el => {
      if (el.getBoundingClientRect().top < window.innerHeight * 1.1) el.classList.add('in');
    }));
    const safety = setTimeout(() => revs.forEach(el => el.classList.add('in')), 1200);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      clearTimeout(safety);
    };
  }, []);

  return (
    <div className="av-landing">
      <Nav />
      <Hero />
      <BoxSection />
      <FeaturesIntro />
      <DriveTime />
      <CustomMenus />
      <Booking />
      <TaxSeason />
      <Connections />
      <Pricing />
      <Founder />
      <Footer />
    </div>
  );
};

const Nav = () => (
  <nav>
    <div className="nav-in">
      {/* Sizing lives in CSS (base 250×60 per the handoff's inline
          style) so the ≤640px media query can shrink it — an inline
          style would override the mobile rule. */}
      <Link to="/" className="logo">
        <img src="/imgs/avayble_logo.png" alt="Avayble" />
      </Link>
      <div className="nav-r">
        <Link to="/login" className="lnk">Log in</Link>
        <Link to="/signup" className="btn btn-solid">Start free trial →</Link>
      </div>
    </div>
  </nav>
);

const Hero = () => (
  <header className="hero" data-screen-label="Hero">
    <div className="wrap reveal">
      <h1>Your Massage Business. <em>Your Rules.</em></h1>
      <p className="hero-sub">Built strictly for the independent massage therapist, Avayble adapts to the unique shape of your business. Ditch the clinic-first software and get a platform that actually understands how you work.</p>
    </div>
    <div className="hero-scene reveal" aria-hidden="true">
      <img className="scene" src="/imgs/landing/illus/pano-left.png" alt="" />
      <div className="tablet">
        <div className="tablet-screen">
          <div className="tab-head"><span className="t1">Today's rhythm</span><span className="t2">Tue</span></div>
          <img className="tab-map" src="/imgs/landing/illus/map.png" alt="" />
          <div className="tab-appt"><span className="t">9:00 AM</span><span><span className="nm">Diane R.</span><br /><span className="meta">90 min · in-home</span></span></div>
          <div className="tab-chip">↓ 22 min drive · fits</div>
          <div className="tab-appt"><span className="t">11:00 AM</span><span><span className="nm">The Ritz · Rm 412</span><br /><span className="meta">60 min · hotel call</span></span></div>
          <div className="tab-chip">↓ 14 min drive · fits</div>
          <div className="tab-appt"><span className="t">1:30 PM</span><span><span className="nm">Aligned Chiro</span><br /><span className="meta">Your rented room · 3 clients</span></span></div>
        </div>
      </div>
      <img className="scene" src="/imgs/landing/illus/pano-right.png" alt="" />
    </div>
    <div className="hero-cta below reveal">
      <div>
        <Link to="/signup" className="btn btn-solid" style={{ padding: '16px 34px', fontSize: '1.02rem' }}>Start your free trial</Link>
        <p className="cta-note">7 days free, then $39.99/month. No card required.</p>
      </div>
    </div>
  </header>
);

const BoxSection = () => (
  <section className="sec band" data-screen-label="You don't fit into a box">
    <div className="wrap">
      <div className="prose-col reveal">
        <span className="badge"><svg viewBox="0 0 24 24"><path d="M3 8l9-5 9 5v8l-9 5-9-5z"></path><path d="M3 8l9 5 9-5"></path><path d="M12 13v8"></path></svg></span>
        <h2>You Don't Fit Into a Box. <em>Your Software Shouldn't Either.</em></h2>
        <p>Most booking software is built for clinics, not individuals. But you aren't a clinic. Maybe you rent a room in a day spa, work out of a chiropractic office, provide mobile in-home sessions, or handle corporate events. Maybe you do a mix of them all.</p>
        <p>Whatever shape your business takes, Avayble holds it, supports it, and makes you more efficient and professional.</p>
      </div>
      <div className="duo-scenes reveal" aria-hidden="true">
        <img className="illu" src="/imgs/landing/illus/scene-inhome.png" alt="In-home massage session" />
        <img className="illu" src="/imgs/landing/illus/scene-clinic.png" alt="In-studio massage session" />
      </div>
    </div>
  </section>
);

const FeaturesIntro = () => (
  <div className="sec-intro reveal" data-screen-label="Features intro">
    <div className="wrap">
      <h2>Features Built Around <em>Your Workflow</em></h2>
    </div>
  </div>
);

const DriveTime = () => (
  <section className="sec feature illu-row" data-screen-label="Drive-time scheduling">
    <div className="wrap">
      <div className="feature-grid reveal">
        <div className="feature-copy">
          <span className="badge"><svg viewBox="0 0 24 24"><path d="M4 16l1.2-4.4A2 2 0 0 1 7.1 10h9.8a2 2 0 0 1 1.9 1.6L20 16"></path><path d="M3.5 16h17a1 1 0 0 1 1 1v2.4a.8.8 0 0 1-.8.8H19l-.4-1.4h-13L5 20.2H3.3a.8.8 0 0 1-.8-.8V17a1 1 0 0 1 1-1z"></path><circle cx="7.2" cy="18.2" r=".4"></circle><circle cx="16.8" cy="18.2" r=".4"></circle><path d="M8 7.5h8"></path><path d="M10.5 4.5h3"></path></svg></span>
          <h2>Drive-Time Aware Scheduling</h2>
          <p>Perfect for the mobile therapist. Avayble's booking engine has drive-time awareness baked right in. When offering availability to new bookings, the system curates your schedule based on actual traffic data, drive lengths, and rush-hour patterns between appointments. Never scramble across town or double-book a timeframe again. Avayble acts as your personal concierge, ensuring you only accept bookings you can comfortably make.</p>
        </div>
        <div className="feature-art">
          <img className="illu" src="/imgs/landing/illus/scene-driving.png" alt="Therapist driving to an appointment with the route on the dash" />
        </div>
      </div>
    </div>
  </section>
);

const CustomMenus = () => (
  <section className="sec band-lt feature stacked" data-screen-label="Custom menus">
    <div className="wrap">
      <div className="feature-grid reveal">
        <div className="feature-copy">
          <span className="badge"><svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="2.5"></rect><path d="M8 8h5M8 12h5M8 16h3"></path><path d="M15.5 15.2l1.2 1.2 2.3-2.6"></path></svg></span>
          <h2>One Price Board for Every Client</h2>
          <p>Your pricing shouldn't be one rigid list. With Avayble, your prices live on one simple board: Public prices for your booking page, and prices of their own for the clients who need them:</p>
          <ul className="feat-list">
            <li>Give a place its own prices: everyone booked at the clinic pays the clinic's.</li>
            <li>Give students, a company or your regulars their own prices, or a discount that moves with your Public prices.</li>
            <li>Keep loyal, long-term clients at the prices they've always paid.</li>
          </ul>
          <p>Whatever your situation, Avayble handles it with ease.</p>
        </div>
        <div className="feature-art">
          <div className="browser" aria-hidden="true"><div className="bar"><span></span><span></span><span></span></div><img src="/imgs/landing/menus-page.png" alt="The Avayble price board" /></div>
        </div>
      </div>
    </div>
  </section>
);

const Booking = () => (
  <section className="sec feature stacked" data-screen-label="Frictionless booking">
    <div className="wrap">
      <div className="feature-grid reveal">
        <div className="feature-copy">
          <span className="badge"><svg viewBox="0 0 24 24"><rect x="2.5" y="5.5" width="19" height="13" rx="2.5"></rect><path d="M2.5 9.5h19"></path><path d="M6 14.5h4"></path></svg></span>
          <h2>Frictionless Booking &amp; Payments</h2>
          <p>Every therapist gets a personalized, public-facing booking page. First-time clients see your Public prices and book instantly, with accounts created automatically via simple SMS text verification. Regulars can easily log into their portal to manage appointments. Want to sell packages or run memberships? Avayble integrates directly with Stripe so you can process payments your way.</p>
        </div>
        <div className="feature-art">
          <div className="browser" aria-hidden="true"><div className="bar"><span></span><span></span><span></span></div><img src="/imgs/landing/booking-page.png" alt="Avayble public booking page" /></div>
        </div>
      </div>
    </div>
  </section>
);

const TaxSeason = () => (
  <section className="sec band-lt feature reverse" data-screen-label="Tax season">
    <div className="wrap">
      <div className="feature-grid reveal">
        <div className="feature-art">
          <div className="phone" aria-hidden="true"><div className="screen"><img className="app-shot" src="/imgs/landing/app-reports.png" alt="Avayble income and mileage reports" /></div></div>
        </div>
        <div className="feature-copy">
          <span className="badge"><svg viewBox="0 0 24 24"><path d="M4 20V10"></path><path d="M10 20V4"></path><path d="M16 20v-8"></path><path d="M22 20H2"></path></svg></span>
          <h2>Tax Season, Simplified</h2>
          <p>Stop dreading taxes. Avayble effortlessly tracks your income, expenditures, and mileage. When tax season arrives, your reports are ready at the click of a mouse or the tap of a finger.</p>
        </div>
      </div>
    </div>
  </section>
);

const Connections = () => (
  <section className="sec feature" data-screen-label="Connections">
    <div className="wrap">
      <div className="feature-grid reveal">
        <div className="feature-copy">
          <span className="badge"><svg viewBox="0 0 24 24"><path d="M21 11.5a8.5 7.5 0 0 1-8.5 7.5c-1.1 0-2.2-.2-3.1-.5L4 20l1.6-3.6A7.3 7.3 0 0 1 4 11.5 8.5 7.5 0 0 1 12.5 4 8.5 7.5 0 0 1 21 11.5z"></path><path d="M12.5 14s-2.8-1.7-2.8-3.5c0-.9.7-1.6 1.5-1.6.6 0 1.1.3 1.3.8.2-.5.7-.8 1.3-.8.8 0 1.5.7 1.5 1.6 0 1.8-2.8 3.5-2.8 3.5z"></path></svg></span>
          <h2>Enhance Your Connections, <em>Don't Automate Them Away</em></h2>
          <p>Massage therapy is an occupation rooted in human connection. While Avayble tracks your clients' rebooking engagements and prompts you with outreach opportunities, it doesn't replace your personal touch. You keep the relationship on your own phone and email. Avayble simply facilitates and improves your communication, ensuring you never automate the life out of your client relationships.</p>
        </div>
        <div className="feature-art">
          <div className="phone" aria-hidden="true"><div className="screen"><img className="app-shot" src="/imgs/landing/outreach-shortlist.png" alt="Avayble Smart Shortlist — outreach opportunities" /></div></div>
        </div>
      </div>
    </div>
  </section>
);

const Pricing = () => (
  <section className="sec band" id="start" data-screen-label="Pricing">
    <div className="wrap">
      <div className="prose-col reveal">
        <span className="badge"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v10M14.5 9c-.5-1-1.4-1.5-2.5-1.5-1.4 0-2.5.9-2.5 2.1 0 3 5 1.7 5 4.7 0 1.2-1.1 2.1-2.5 2.1-1.1 0-2-.5-2.5-1.5"></path></svg></span>
        <h2>Simple, Transparent Pricing</h2>
        <p style={{ textAlign: 'center' }}>No hidden fees. No premium tiers to unlock.</p>
        <p className="price-line">Just $39.99 a month for <em>absolutely everything.</em></p>
        <div className="hero-cta" style={{ marginTop: 32 }}>
          <div>
            <Link to="/signup" className="btn btn-solid">Start your free trial</Link>
            <p className="cta-note">7 days free, then $39.99/month. No card required.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Founder = () => (
  <section className="sec" data-screen-label="Built by a therapist">
    <div className="wrap">
      <div className="founder-grid reveal">
        <div>
          <img className="founder-photo" src="/imgs/landing/illus/ivan-founder.png" alt="Ivan, founder of Avayble" />
        </div>
        <div className="founder-body">
          <span className="badge"><svg viewBox="0 0 24 24"><path d="M12 20s-6.5-3.8-8.5-7.6C2 9.6 3.6 6.5 6.6 6.5c1.9 0 3.4 1 4.2 2.6.2.4.4.4.6 0 .8-1.6 2.3-2.6 4.2-2.6 3 0 4.6 3.1 3.1 5.9C16.5 16.2 12 20 12 20z"></path></svg></span>
          <h2>Built by a Therapist, <em>for Therapists</em></h2>
          <p>Avayble was created by a massage therapist nerdy enough to love coding. Because of this, your voice actually matters. Feature requests and bug reports aren't just welcomed—they are actively encouraged to make the platform better every single day.</p>
          <p>Ivan has been a massage therapist since 2014 and has worked in a large variety of capacities in the industry — from rehab clinics and day spas, as a lead at luxury hotels, private mobile practice, and now as an app designer serving the massage therapist community.</p>
        </div>
      </div>
      <div className="tech-note reveal">
        <p><b>A Note on Our Tech:</b> Avayble is currently a Progressive Web App (PWA). This means it can be easily saved to your Apple or Android home screen to behave just like a native app. As our community grows, we will launch fully native apps to unlock even more features, like importing your phone contacts directly into the system!</p>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer>
    <div className="wrap foot-in">
      <Link to="/" className="foot-logo"><img src="/imgs/avayble_logo.png" alt="Avayble" /></Link>
      <div className="foot-links">
        <Link to="/login">Log in</Link>
        <Link to="/signup">Start free trial</Link>
        <a href="/privacy-policy.html">Privacy</a>
        <a href="/terms-of-service.html">Terms</a>
        <a href="/sms-consent-policy.html">SMS Policy</a>
      </div>
      <div>© 2026 Avayble LLC</div>
    </div>
  </footer>
);

export default Landing;
